import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Key, Axe, Leaf } from 'lucide-react'; // Using Axe for Labris (Power), Key for Hekate, Leaf for Olive
import logo from '../assets/turet-logo.jpg';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const LogoMeaningPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    return (
        <div className="bg-[#0f172a] text-gray-200 min-h-screen relative overflow-hidden">

            {/* Background Effects */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute top-0 left-0 -ml-20 w-[800px] h-[800px] bg-emerald-500/10 rounded-full blur-[120px] opacity-20" />
                <div className="absolute bottom-0 right-0 -mr-20 w-[600px] h-[600px] bg-yellow-500/10 rounded-full blur-[100px] opacity-20" />
            </div>

            <Navbar />

            <main className="pt-32 pb-20 px-6 relative z-10">
                <div className="container mx-auto">

                    {/* Intro Section */}
                    <div className="max-w-4xl mx-auto text-center mb-20">

                        <motion.h1
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-yellow-100 to-emerald-200 mb-8 font-heading leading-tight"
                        >
                            Logomuzun Derinliği
                        </motion.h1>
                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2 }}
                            className="text-gray-400 text-base leading-relaxed font-light"
                        >
                            Muğla Sıtkı Koçman Üniversitesi Turist Rehberliği Topluluğu (TURET), kurumsal kimliğini temsil etmek üzere tasarlanan logosunda, faaliyet gösterdiği coğrafyanın binlerce yıllık kültürel mirasını temel almıştır. Logomuzda yer alan her bir bileşen, derin tarihi referanslar taşımakta ve topluluğun misyonunu yansıtmaktadır.
                        </motion.p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">

                        {/* Logo Display (Center/Left) */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            className="lg:col-span-12 xl:col-span-5 flex flex-col items-center"
                        >
                            <div className="relative group w-full max-w-md mx-auto aspect-square flex items-center justify-center p-8">
                                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 to-emerald-500/20 rounded-full blur-3xl group-hover:blur-[100px] transition-all duration-700" />
                                <div className="absolute inset-4 border border-cyan-500/20 rounded-full animate-[spin_10s_linear_infinite]" />
                                <div className="absolute inset-12 border border-purple-500/20 rounded-full animate-[spin_15s_linear_infinite_reverse]" />

                                <img
                                    src={logo}
                                    alt="TURET Logo"
                                    className="w-full h-full object-cover rounded-full relative z-10 drop-shadow-2xl shadow-2xl shadow-cyan-500/20"
                                />
                            </div>
                            <p className="mt-8 text-center text-gray-500 text-xs italic max-w-md">
                                "TURET logosu; gücü, gizemi ve bilgeliği birleştirerek, bir öğrenci topluluğunun ötesinde, bölgenin kadim mirasının taşıyıcısı ve anlatıcısı olma vizyonunu kurumsal bir dille ifade etmektedir."
                            </p>
                        </motion.div>

                        {/* Meaning Cards (Right) */}
                        <motion.div
                            variants={containerVariants}
                            initial="hidden"
                            whileInView="visible"
                            viewport={{ once: true }}
                            className="lg:col-span-12 xl:col-span-7 space-y-6"
                        >
                            <MeaningCard
                                icon={<Axe className="w-8 h-8 text-yellow-400" />}
                                title="Labris (Çift Taraflı Balta)"
                                desc="Logonun merkezinde yer alan Labris, Antik Karya Bölgesi'nin (günümüz Muğla'sı) en belirgin sembollerinden biridir. Güç, otorite ve bölgenin antik inanışlarını temsil eden bu sembol, aynı zamanda Labranda Kutsal Alanı gibi önemli arkeolojik merkezlere doğrudan bir atıftır."
                                color="yellow"
                            />

                            <MeaningCard
                                icon={<Key className="w-8 h-8 text-purple-400" />}
                                title="Hekate'nin Anahtarları"
                                desc="Kesişen anahtarlar, mitolojide yolların, kavşakların ve gizli bilginin tanrıçası olarak bilinen Hekate'yi simgeler. Antik dünyanın en önemli Hekate kült merkezlerinden birinin Lagina'da (Yatağan) bulunması, bu sembolün TURET için önemini artırmaktadır. Anahtarlar, tarihin ve bilgeliğin kapılarını aralama misyonunu ifade eder."
                                color="purple"
                            />

                            <MeaningCard
                                icon={<Leaf className="w-8 h-8 text-emerald-400" />}
                                title="Zeytin Dalları"
                                desc="Tüm sembolleri çevreleyen zeytin dalları, Muğla coğrafyasının ayrılmaz bir parçası olan bereketi, bilgeliği ve barışı temsil eder. Turist rehberliğinin kültürler arası bir anlayış ve barış köprüsü kurma işlevine vurgu yapar."
                                color="emerald"
                            />
                        </motion.div>
                    </div>

                </div>
            </main>


        </div>
    );
};

const MeaningCard = ({ icon, title, desc, color }) => (
    <motion.div
        variants={{
            hidden: { x: 50, opacity: 0 },
            visible: { x: 0, opacity: 1 }
        }}
        className="group relative bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-3xl hover:bg-white/[0.08] transition-all duration-300"
    >
        <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-${color}-500 to-transparent rounded-l-3xl opacity-50 group-hover:opacity-100 transition-opacity`} />

        <div className="flex gap-4 items-start">
            <div className={`p-3 rounded-2xl bg-${color}-500/10 border border-${color}-500/20 shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                {icon}
            </div>
            <div>
                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-cyan-200 transition-colors">
                    {title}
                </h3>
                <p className="text-gray-400 leading-relaxed text-xs group-hover:text-gray-300 transition-colors">
                    {desc}
                </p>
            </div>
        </div>
    </motion.div>
);

export default LogoMeaningPage;
